/**
 * AwaitQuoteResponse · iter303ak-fix23d — Quote Sent · Follow-up · Action 20
 * ─────────────────────────────────────────────────────────────────────────
 * Rewritten to Trevor's spec: per-child (per-quote) decision picker with
 * three states (accepted / pre-accepted / declined) plus an acceptance
 * type dropdown at the top. Handles mixed decisions gracefully.
 *
 * Confirm routing (parent appraisal_status transition):
 *   • ANY child = accepted           → status = "Accepted"
 *   • ALL children = declined        → status = "Declined"
 *   • Mix of pre_accepted + declined → status stays "Quote Sent",
 *                                      quote_followup_due_at bumps to
 *                                      the max of per-child follow-up
 *                                      dates
 *   • All pre_accepted               → same as above
 *   • Any pending (undecided)        → advance blocked with a message
 *
 * Uses `PATCH /appraisals/{id}/status` for the status transition
 * (existing endpoint) and `PATCH /appraisals/{id}` for the
 * child_decisions + acceptance / declined summaries (allowlisted in
 * fix23d).
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Clock, AlertCircle, ChevronRight } from 'lucide-react';
import QuoteDecisionPicker from './QuoteDecisionPicker';
import { OnlineAcceptanceCard } from '@/components/OnlineAcceptanceCard';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const ACCEPTANCE_TYPES = ['Email', 'SMS', 'Deposit received', 'Combination', 'Online'];

const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function AwaitQuoteResponse({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const app = capturedData?.appraisal || capturedData?.appraisal_record || {};
  const followupDueAt = capturedData?.['appraisal.quote_followup_due_at']
    || app?.quote_followup_due_at
    || null;

  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState(value?.decisions || app?.child_decisions || {});
  const [acceptanceType, setAcceptanceType] = useState(value?.acceptance_type || app?.acceptance_type || 'Email');
  const [entry, setEntry] = useState(value?.acceptance_entry || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const seededRef = useRef(false);
  const [onlinePending, setOnlinePending] = useState(false);

  // Client's online pre-acceptance → their picks become accepted, the rest declined (office reviews, then Confirm).
  const applyOnline = (oa) => {
    const next = { ...decisions };
    for (const c of children) {
      const cid = c.id || c._id;
      const pickedByClient = (oa.quote_ids || []).includes(cid);
      if (pickedByClient || !next[cid]?.decision) {
        next[cid] = { decision: pickedByClient ? 'accepted' : 'declined', quote_type_ref: c.quote_type_ref || '', variant_label: c.variant_label || '', appraisal_no: c.appraisal_no || null };
      }
    }
    setDecisions(next);
    setAcceptanceType('Online');
  };

  const loadChildren = useCallback(async () => {
    if (!appraisalId) { setLoading(false); return; }
    try {
      const { data } = await api.get(`/appraisals/${appraisalId}/children`);
      let kids = Array.isArray(data?.children) ? data.children : [];
      // Synthesise a single virtual row for appraisals with no children.
      // Batch T follow-up · workflow integration (2026-02): read the
      // Deposit Engine value first, falling back to legacy for BC.
      if (kids.length === 0) {
        kids = [{
          id: appraisalId,
          appraisal_no: app.appraisal_no || null,
          quote_type_ref: app.quote_type_ref || 'This appraisal',
          variant_label: app.variant_label || '',
          quote_total: app.quote_total ?? 0,
          deposit_amount: app.deposit_engine_amount ?? app.deposit_amount ?? 0,
          deposit_engine_amount: app.deposit_engine_amount ?? null,
          _synthetic: true,
        }];
      }
      setChildren(kids);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to load quote children');
    } finally { setLoading(false); }
  }, [appraisalId, app.appraisal_no, app.quote_type_ref, app.variant_label, app.quote_total, app.deposit_amount]);

  useEffect(() => { loadChildren(); }, [loadChildren]);

  // Persist decisions + accept type upstream on any change (runner writes
  // to captured_data — the confirm-step PATCHes to Mongo).
  useEffect(() => {
    if (onChange) {
      onChange({ decisions, acceptance_type: acceptanceType, acceptance_entry: entry });
    }
  }, [decisions, acceptanceType, entry, onChange]);

  // Countdown pill for the follow-up reminder date.
  const countdown = useMemo(() => {
    if (!followupDueAt) return null;
    const due = new Date(followupDueAt);
    const now = new Date();
    const diffMs = due - now;
    const dayMs = 24 * 60 * 60 * 1000;
    const days = Math.round(diffMs / dayMs);
    if (diffMs < -dayMs) return { label: `${Math.abs(days)}d overdue`, cls: 'bg-rose-100 text-rose-800 border-rose-300' };
    if (diffMs < 0) return { label: `Overdue`, cls: 'bg-rose-100 text-rose-800 border-rose-300' };
    if (diffMs < dayMs) return { label: `Due today`, cls: 'bg-amber-100 text-amber-800 border-amber-300' };
    return { label: `Due in ${days}d`, cls: 'bg-sky-100 text-sky-800 border-sky-300' };
  }, [followupDueAt]);

  // Summary counters + advance-target computation.
  const counts = useMemo(() => {
    const out = { accepted: [], pre_accepted: [], declined: [], pending: [] };
    for (const c of children) {
      const cid = c.id || c._id;
      const d = decisions[cid]?.decision;
      if (d === 'accepted') out.accepted.push(cid);
      else if (d === 'pre_accepted') out.pre_accepted.push(cid);
      else if (d === 'declined') out.declined.push(cid);
      else out.pending.push(cid);
    }
    return out;
  }, [children, decisions]);

  const advanceTarget = useMemo(() => {
    if (children.length === 0) return { kind: 'idle' };
    if (counts.pending.length > 0) return { kind: 'blocked_pending', pending: counts.pending.length };
    if (counts.accepted.length > 0) return { kind: 'accepted' };
    if (counts.declined.length === children.length) return { kind: 'declined' };
    // Mix of pre_accepted + declined (or all pre_accepted) → stay
    return { kind: 'stay_followup' };
  }, [children, counts]);

  // iter303ak-fix23d · Deposit-received auto-select: if the operator
  // picks "Deposit received" as the acceptance type, that's an implicit
  // "all accepted" signal. Auto-fill any pending rows with accepted so
  // the operator doesn't have to bulk-click. Only fires once per session.
  useEffect(() => {
    if (acceptanceType !== 'Deposit received' || seededRef.current || children.length === 0) return;
    const pending = children.filter((c) => !decisions[c.id || c._id]);
    if (pending.length === children.length) {
      // Everything pending — seed all as accepted
      const next = {};
      for (const c of children) {
        const cid = c.id || c._id;
        next[cid] = {
          decision: 'accepted',
          quote_type_ref: c.quote_type_ref || '',
          variant_label: c.variant_label || '',
          appraisal_no: c.appraisal_no || null,
        };
      }
      setDecisions(next);
      seededRef.current = true;
    }
  }, [acceptanceType, children, decisions]);

  const buildSummary = useCallback((state) => {
    return children
      .filter((c) => decisions[c.id || c._id]?.decision === state)
      .map((c) => [c.quote_type_ref, c.variant_label].filter(Boolean).join(' · '))
      .join('; ');
  }, [children, decisions]);

  const maxFollowupDate = useCallback(() => {
    const dates = children
      .map((c) => decisions[c.id || c._id])
      .filter((d) => d?.decision === 'pre_accepted' && d.followup_date)
      .map((d) => d.followup_date);
    if (dates.length === 0) return null;
    return dates.sort().pop();
  }, [children, decisions]);

  const confirm = async () => {
    if (advanceTarget.kind === 'blocked_pending') {
      setErr(`${advanceTarget.pending} quote(s) still pending — pick a decision for each row before confirming.`);
      return;
    }
    if (!appraisalId) {
      onNext();
      return;
    }
    setSaving(true); setErr('');
    try {
      const nowIso = new Date().toISOString();
      const patchBody = {
        child_decisions: decisions,
        acceptance_type: acceptanceType,
        accepted_child_ids: counts.accepted,
        accepted_quote_summary: buildSummary('accepted'),
      };
      // Summary blocks for legacy report tokens.
      if (counts.accepted.length > 0) {
        patchBody.acceptance = {
          type: acceptanceType,
          date: todayLocal(),
          entry,
          stamped_at: nowIso,
        };
        patchBody.contract_accepted_date = todayLocal();
      }
      if (advanceTarget.kind === 'declined') {
        patchBody.declined = {
          kind: 'client_declined',
          date: todayLocal(),
          notes: entry,
          stamped_at: nowIso,
        };
        patchBody.declined_date = todayLocal();
      }
      if (advanceTarget.kind === 'stay_followup') {
        const nextFollowup = maxFollowupDate();
        if (nextFollowup) patchBody.quote_followup_due_at = nextFollowup;
      }
      await api.patch(`/appraisals/${appraisalId}`, patchBody);
      if (onlinePending && counts.accepted.length > 0) {
        await api.post(`/appraisals/${appraisalId}/online-acceptance/review`, { action: 'confirmed' }).catch(() => {});
      }

      // Status transition (skip when staying in Quote Sent).
      if (advanceTarget.kind === 'accepted') {
        await api.patch(`/appraisals/${appraisalId}/status`, {
          appraisal_status: 'Accepted',
          contract_accepted_date: todayLocal(),
          via: 'workflow_runner',
        });
      } else if (advanceTarget.kind === 'declined') {
        await api.patch(`/appraisals/${appraisalId}/status`, {
          appraisal_status: 'Declined',
          via: 'workflow_runner',
        });
      }
      onChange && onChange({
        decisions,
        acceptance_type: acceptanceType,
        acceptance_entry: entry,
        outcome: advanceTarget.kind,
      });
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to save decision');
    } finally { setSaving(false); }
  };

  const skipForNow = () => {
    onChange && onChange({ decisions, acceptance_type: acceptanceType, acceptance_entry: entry, outcome: 'skipped' });
    onNext();
  };

  // Confirm button label + colour reflects the routing outcome.
  const confirmLabel = {
    accepted:        'Confirm → Accepted',
    declined:        'Confirm → Declined',
    stay_followup:   'Confirm — set new follow-up',
    blocked_pending: 'Confirm',
    idle:            'Confirm',
  }[advanceTarget.kind];
  const confirmClass = {
    accepted:        'bg-emerald-600 hover:bg-emerald-700',
    declined:        'bg-rose-600 hover:bg-rose-700',
    stay_followup:   'bg-amber-600 hover:bg-amber-700',
    blocked_pending: 'bg-slate-400 cursor-not-allowed',
    idle:            'bg-slate-400 cursor-not-allowed',
  }[advanceTarget.kind];

  return (
    <div className="space-y-4" data-testid="wf-await-quote-response">
      <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-sky-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-sky-900 flex items-center gap-2">
              Awaiting Client Response
              {countdown && (
                <span className={`text-[10px] font-bold uppercase tracking-widest border rounded px-1.5 py-0.5 ${countdown.cls}`} data-testid="wf-await-countdown">
                  {countdown.label}
                </span>
              )}
            </div>
            <div className="text-sm text-sky-800 mt-1">
              {params.prompt || 'This is the follow-up for the whole quote bundle (all variants). Pick a decision per quote below — mix and match freely.'}
            </div>
            {followupDueAt && (
              <div className="text-xs text-sky-700 mt-1">
                Reminder set for <b>{new Date(followupDueAt).toLocaleString()}</b>
              </div>
            )}
          </div>
        </div>
      </div>

      {appraisalId && (
        <OnlineAcceptanceCard api={api} jobId={appraisalId} compact onApply={applyOnline}
          onState={(s) => setOnlinePending(s?.acceptance?.status === 'pending_confirmation')} />
      )}

      <div className="p-3 border border-slate-200 rounded-lg bg-white">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Acceptance type <span className="text-slate-400">(applies to accepted rows)</span></label>
            <select
              value={acceptanceType}
              onChange={(e) => setAcceptanceType(e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="wf-await-acceptance-type"
            >
              {ACCEPTANCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="text-[10px] text-slate-500 mt-1">
              Email is default. Deposit received will auto-mark all rows as accepted.
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Note (optional)</label>
            <input
              type="text"
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              placeholder="e.g. spoke to client at 2pm — accepted Option A"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="wf-await-note"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-slate-400 italic">Loading quote package…</div>
      ) : (
        <QuoteDecisionPicker
          children={children}
          decisions={decisions}
          onChange={setDecisions}
          headerLabel="Per-quote decision"
        />
      )}

      {advanceTarget.kind === 'blocked_pending' && (
        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
          <b>{advanceTarget.pending}</b> quote(s) still pending — pick a decision for every row (or use a Bulk button).
        </div>
      )}
      {advanceTarget.kind === 'stay_followup' && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> No quotes accepted yet — staying in <b>Quote Sent</b>. Reminder bumps to <b>{maxFollowupDate() || '(no date set)'}</b>.
        </div>
      )}
      {advanceTarget.kind === 'accepted' && (
        <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" /> {counts.accepted.length} accepted · {counts.pre_accepted.length} pre-accepted · {counts.declined.length} declined → moving to <b>Accepted</b>.
        </div>
      )}
      {advanceTarget.kind === 'declined' && (
        <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-1.5">
          <XCircle className="w-3.5 h-3.5" /> All {counts.declined.length} quote(s) declined → moving to <b>Declined</b>.
        </div>
      )}

      {err && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-await-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-await-cancel">Cancel</button>
          )}
          <button
            onClick={skipForNow}
            disabled={saving}
            className="px-3 py-1.5 rounded-md text-sm bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="wf-await-skip"
            title="Advance without touching the appraisal — the follow-up reminder stays as-is"
          >
            <Clock className="w-4 h-4" /> Skip for now
          </button>
          <button
            onClick={confirm}
            disabled={saving || advanceTarget.kind === 'blocked_pending' || advanceTarget.kind === 'idle'}
            className={`px-4 py-1.5 rounded-md text-sm text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 ${confirmClass}`}
            data-testid="wf-await-confirm"
          >
            {saving ? 'Saving…' : confirmLabel} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
